import * as React from "react";
import PhoneInputWithCountrySelect, { type Country } from "react-phone-number-input";
import "react-phone-number-input/style.css";
import { cn } from "@/lib/utils";

export interface PhoneInputProps {
  value?: string;
  onChange: (value: string | undefined) => void;
  /** Classes for the bordered control (width, background). */
  className?: string;
  placeholder?: string;
  disabled?: boolean;
  international?: boolean;
  defaultCountry?: Country;
  /** Applies `border-destructive` (COMPONENT_PATTERNS.md inline errors). */
  hasError?: boolean;
}

/**
 * A phone number field styled as a shadcn `Input`: same height, border,
 * radius, 12px left inset (the country flag sits where an Input's text
 * starts), text size (16px on mobile so iOS does not zoom, 14px from `md`)
 * and focus ring. The library renders a `div.PhoneInput` root holding the
 * country select and the `<input>`, so the border lives on that root and the
 * disabled look is driven by `has-[:disabled]` — a div never matches
 * `:disabled` itself.
 */
const PhoneInput = React.forwardRef<HTMLInputElement, PhoneInputProps>(
  (
    {
      className,
      placeholder = "Enter phone number",
      international = true,
      defaultCountry = "US",
      disabled,
      hasError,
      ...props
    },
    ref,
  ) => {
    return (
      <PhoneInputWithCountrySelect
        {...props}
        ref={ref}
        international={international}
        defaultCountry={defaultCountry}
        placeholder={placeholder}
        disabled={disabled}
        // Passed through to the number <input> (react-phone-number-input forwards unknown
        // props): the error is announced, not only coloured.
        aria-invalid={hasError || undefined}
        className={cn(
          "flex h-10 w-full items-center rounded-md border border-input bg-background pl-3 ring-offset-background",
          "focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2",
          "has-[:disabled]:cursor-not-allowed has-[:disabled]:opacity-50",
          "[&_.PhoneInputCountrySelect]:cursor-pointer [&_.PhoneInputCountrySelect:disabled]:cursor-not-allowed",
          "[&_.PhoneInputCountrySelectArrow]:text-muted-foreground [&_.PhoneInputCountrySelectArrow]:opacity-80",
          "[&_.PhoneInputInput]:h-full [&_.PhoneInputInput]:min-w-0 [&_.PhoneInputInput]:border-0 [&_.PhoneInputInput]:bg-transparent",
          "[&_.PhoneInputInput]:pl-2 [&_.PhoneInputInput]:pr-3 [&_.PhoneInputInput]:text-base md:[&_.PhoneInputInput]:text-sm",
          "[&_.PhoneInputInput]:outline-none [&_.PhoneInputInput]:placeholder:text-muted-foreground",
          "[&_.PhoneInputInput]:disabled:cursor-not-allowed",
          hasError && "border-destructive",
          className,
        )}
      />
    );
  },
);

PhoneInput.displayName = "PhoneInput";

export { PhoneInput };
